// ============================================================================
// BrightPath - NotificationFilters Component
// BP-12-03: Notifications Frontend
// ============================================================================

import React from 'react';
import { Filter, X } from 'lucide-react';
import {
  NotificationCategory,
  NotificationPriority,
  NOTIFICATION_CATEGORY_CONFIG,
  NOTIFICATION_PRIORITY_CONFIG,
} from '../types/notificationsTypes';
import type { NotificationFilterState } from '../types/notificationsTypes';

// ----------------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------------

interface NotificationFiltersProps {
  filters: NotificationFilterState;
  onFilterChange: (filters: Partial<NotificationFilterState>) => void;
  onReset?: () => void;
  showReset?: boolean;
  compact?: boolean;
}

// ----------------------------------------------------------------------------
// Filter Chip Component
// ----------------------------------------------------------------------------

interface FilterChipProps {
  label: string;
  isActive: boolean;
  onClick: () => void;
  variant?: 'default' | 'category';
  color?: string;
}

const FilterChip: React.FC<FilterChipProps> = ({
  label,
  isActive,
  onClick,
  variant = 'default',
}) => {
  return (
    <button
      onClick={onClick}
      className={`
        px-3 py-1.5 rounded-full text-sm font-medium
        transition-all duration-200
        ${isActive
          ? 'bg-sage text-white'
          : 'bg-cream text-navy hover:bg-cream-dark'
        }
      `}
    >
      {label}
    </button>
  );
};

// ----------------------------------------------------------------------------
// Select Component
// ----------------------------------------------------------------------------

interface FilterSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
}

const FilterSelect: React.FC<FilterSelectProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Select...',
}) => {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="
        px-3 py-2 rounded-lg text-sm
        bg-cream border border-warm-gray/20
        text-navy focus:border-sage focus:ring-1 focus:ring-sage
        appearance-none cursor-pointer
        pr-8 bg-no-repeat
      "
      style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%238B8178' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
        backgroundPosition: 'right 8px center',
      }}
    >
      <option value="">{placeholder}</option>
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
};

// ----------------------------------------------------------------------------
// Main Component
// ----------------------------------------------------------------------------

export const NotificationFilters: React.FC<NotificationFiltersProps> = ({
  filters,
  onFilterChange,
  onReset,
  showReset = true,
  compact = false,
}) => {
  // Check if any filters are active
  const hasActiveFilters =
    filters.category !== 'all' ||
    filters.isRead !== 'all' ||
    filters.priority !== 'all' ||
    filters.dateRange !== 'all';

  // Category options
  const categoryOptions = [
    { value: 'all', label: 'All Categories' },
    ...Object.entries(NOTIFICATION_CATEGORY_CONFIG).map(([key, config]) => ({
      value: key,
      label: config.label,
    })),
  ];

  // Read status options
  const readStatusOptions = [
    { value: 'all', label: 'All' },
    { value: 'unread', label: 'Unread' },
    { value: 'read', label: 'Read' },
  ];

  // Priority options
  const priorityOptions = [
    { value: 'all', label: 'All Priorities' },
    ...Object.entries(NOTIFICATION_PRIORITY_CONFIG).map(([key, config]) => ({
      value: key,
      label: config.label,
    })),
  ];

  // Date range options
  const dateRangeOptions = [
    { value: 'all', label: 'All Time' },
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
  ];

  // Compact variant (chips only)
  if (compact) {
    return (
      <div className="flex items-center gap-2 flex-wrap">
        {/* Read status chips */}
        <div className="flex items-center gap-1">
          {readStatusOptions.map((opt) => (
            <FilterChip
              key={opt.value}
              label={opt.label}
              isActive={filters.isRead === opt.value}
              onClick={() =>
                onFilterChange({
                  isRead: opt.value as NotificationFilterState['isRead'],
                })
              }
            />
          ))}
        </div>

        {/* Divider */}
        <div className="w-px h-6 bg-warm-gray/20" />

        {/* Category filter */}
        <FilterSelect
          value={filters.category}
          onChange={(v) =>
            onFilterChange({
              category: v as NotificationFilterState['category'],
            })
          }
          options={categoryOptions}
          placeholder="Category"
        />

        {/* Reset button */}
        {showReset && hasActiveFilters && (
          <button
            onClick={onReset}
            className="
              p-2 rounded-lg
              text-warm-gray hover:text-navy hover:bg-cream
              transition-colors
            "
            title="Reset filters"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  }

  // Full variant
  return (
    <div className="bg-white rounded-xl border border-warm-gray/10 p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-warm-gray" />
          <span className="text-sm font-medium text-navy">Filters</span>
        </div>

        {showReset && hasActiveFilters && (
          <button
            onClick={onReset}
            className="
              text-sm text-warm-gray hover:text-navy
              flex items-center gap-1
              transition-colors
            "
          >
            <X className="w-3 h-3" />
            Clear all
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Category */}
        <div>
          <label className="block text-xs font-medium text-warm-gray mb-1.5">
            Category
          </label>
          <FilterSelect
            value={filters.category}
            onChange={(v) =>
              onFilterChange({
                category: v as NotificationFilterState['category'],
              })
            }
            options={categoryOptions}
          />
        </div>

        {/* Read Status */}
        <div>
          <label className="block text-xs font-medium text-warm-gray mb-1.5">
            Status
          </label>
          <div className="flex gap-1">
            {readStatusOptions.map((opt) => (
              <FilterChip
                key={opt.value}
                label={opt.label}
                isActive={filters.isRead === opt.value}
                onClick={() =>
                  onFilterChange({
                    isRead: opt.value as NotificationFilterState['isRead'],
                  })
                }
              />
            ))}
          </div>
        </div>

        {/* Priority */}
        <div>
          <label className="block text-xs font-medium text-warm-gray mb-1.5">
            Priority
          </label>
          <FilterSelect
            value={filters.priority}
            onChange={(v) =>
              onFilterChange({
                priority: v as NotificationFilterState['priority'],
              })
            }
            options={priorityOptions}
          />
        </div>

        {/* Date Range */}
        <div>
          <label className="block text-xs font-medium text-warm-gray mb-1.5">
            Time Period
          </label>
          <FilterSelect
            value={filters.dateRange}
            onChange={(v) =>
              onFilterChange({
                dateRange: v as NotificationFilterState['dateRange'],
              })
            }
            options={dateRangeOptions}
          />
        </div>
      </div>

      {/* Active filters summary */}
      {hasActiveFilters && (
        <div className="mt-4 pt-4 border-t border-warm-gray/10">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-warm-gray">Active filters:</span>
            
            {filters.category !== 'all' && (
              <span className="px-2 py-1 bg-sage/10 text-sage-dark text-xs rounded-full flex items-center gap-1">
                {NOTIFICATION_CATEGORY_CONFIG[filters.category as NotificationCategory]?.label}
                <button
                  onClick={() => onFilterChange({ category: 'all' })}
                  className="hover:text-sage"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}

            {filters.isRead !== 'all' && (
              <span className="px-2 py-1 bg-sage/10 text-sage-dark text-xs rounded-full flex items-center gap-1">
                {filters.isRead === 'unread' ? 'Unread only' : 'Read only'}
                <button
                  onClick={() => onFilterChange({ isRead: 'all' })}
                  className="hover:text-sage"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}

            {filters.priority !== 'all' && (
              <span className="px-2 py-1 bg-sage/10 text-sage-dark text-xs rounded-full flex items-center gap-1">
                {NOTIFICATION_PRIORITY_CONFIG[filters.priority as NotificationPriority]?.label}
                <button
                  onClick={() => onFilterChange({ priority: 'all' })}
                  className="hover:text-sage"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}

            {filters.dateRange !== 'all' && (
              <span className="px-2 py-1 bg-sage/10 text-sage-dark text-xs rounded-full flex items-center gap-1">
                {dateRangeOptions.find((o) => o.value === filters.dateRange)?.label}
                <button
                  onClick={() => onFilterChange({ dateRange: 'all' })}
                  className="hover:text-sage"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationFilters;

